import * as yup from 'yup';
import type { Address } from '@electricjs/arc';

import i18n from '@/i18n';

export const formatAddressForDisplay = (address: Address) => {
  const streetParts = [address.streetAddress1, address.streetAddress2].filter(
    Boolean
  );
  const locationParts = [
    address.city,
    address.state,
    address.zip,
    address.country,
  ].filter(Boolean);

  return {
    street: streetParts.join(', '),
    location: locationParts.join(', '),
  };
};

/**
 * Creates a Yup validation test that makes a field required when any of the specified
 * other fields have values. This prevents partial form submissions by ensuring
 * all related fields are filled when any field in the group has a value.
 *
 * @param fields - Array of field names to check for values. If any of these
 *                      fields have a non-empty value, the current field becomes required.
 * @param errorMessage - Custom error message to display when validation fails.
 *                       Defaults to 'This field is required'.
 * @returns A Yup string schema with conditional validation test.
 *
 * @example
 * ```typescript
 * // Make streetAddress1 required if city, state, or zip have values
 * streetAddress1: createConditionalTest(['city', 'state', 'zip'])
 *
 * // With custom error message
 * city: createConditionalTest(
 *   ['streetAddress1', 'state', 'zip'],
 *   'City is required when address is provided'
 * )
 * ```
 */
export const createConditionalTest = (
  fields: string[],
  errorMessage: string = i18n.t(
    'storefront:international.firstStep.errors.fieldRequired'
  )
) => {
  return yup
    .string()
    .nullable()
    .test('conditional-required', errorMessage, function (value) {
      const parent = this.parent || {};
      const hasOtherValues = fields.some(field => Boolean(parent[field]));

      if (!hasOtherValues) return true;
      return Boolean(value && value.trim() !== '');
    });
};

/**
 * Creates a Yup validation schema for address forms with configurable validation rules.
 *
 * When `required` is true, creates a strict schema where core address fields (streetAddress1,
 * city, country, zip) are required and others are optional. When `required` is false,
 * creates a conditional schema where fields become required only when other address
 * fields have values, preventing partial form submissions.
 *
 * @param required - Whether to create a strict required schema (true) or conditional
 *                   schema (false). Defaults to true.
 * @returns A Yup object schema with appropriate validation rules for address fields.
 *
 * @example
 * ```typescript
 * // Strict validation - all core fields required
 * const strictSchema = createAddressValidationSchema(true);
 *
 * // Conditional validation - fields required only when others have values
 * const conditionalSchema = createAddressValidationSchema(false);
 *
 * // Usage in form validation
 * const formSchema = yup.object().shape({
 *   name: yup.string().required(),
 *   address: createAddressValidationSchema(false),
 * });
 * ```
 */
export const createAddressValidationSchema = (required: boolean = true) => {
  if (required) {
    const fieldRequiredMessage = i18n.t(
      'storefront:international.firstStep.errors.fieldRequired'
    );

    return yup.object().shape({
      streetAddress1: yup.string().required(fieldRequiredMessage),
      streetAddress2: yup.string().nullable(),
      city: yup.string().required(fieldRequiredMessage),
      state: yup.string().nullable(),
      country: yup.string().required(fieldRequiredMessage),
      zip: yup.string().required(fieldRequiredMessage),
    });
  }

  return yup.object().shape({
    streetAddress1: createConditionalTest([
      'streetAddress2',
      'city',
      'state',
      'country',
      'zip',
    ]),
    streetAddress2: yup.string().nullable(),
    city: createConditionalTest([
      'streetAddress1',
      'streetAddress2',
      'state',
      'country',
      'zip',
    ]),
    state: yup.string().nullable(),
    country: createConditionalTest([
      'streetAddress1',
      'streetAddress2',
      'city',
      'state',
      'zip',
    ]),
    zip: createConditionalTest([
      'streetAddress1',
      'streetAddress2',
      'city',
      'state',
      'country',
    ]),
  });
};
